import { useAuth } from '@teko/auth-react';
import { IsolatedCartType, getBuyNowKey } from '@teko/cart/v2';
import {
  Button,
  CustomLink,
  Flex,
  SubTitle,
  styled,
  useToast,
} from '@teko/ds-desktop';
import { CautionOutlined } from '@teko/ds-icon';
import { logger } from '@teko/log';
import {
  AddToCartType,
  TrackAddToCartParams,
  getTrackingData,
  trackAddToCart,
} from '@teko/tracking';
import { useCartV2, useProductDetailState, useTenantConfig } from 'contexts';
import { tryOpenComboPopupFromDetail } from 'helpers/comboPopup';
import { useRequireLoginForCartAction } from 'hooks';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import { RefObject, useMemo } from 'react';
import { BackInStockConfirmationModal } from '../../shared/BackInStockConfirmationModal';

interface IActionButtonsProps {
  actionButtonsRef: RefObject<HTMLDivElement>;
}

export const ActionButtons = ({ actionButtonsRef }: IActionButtonsProps) => {
  const router = useRouter();
  const { loading: authLoading, user } = useAuth();
  const isLoggedIn = !!user?.userId;
  const requireLogin = useRequireLoginForCartAction();
  const { contactLink, allowBuyNow, epicOmni1551 } = useTenantConfig();
  const {
    product,
    price,
    isOutOfStock,
    actionReady,
    payloadAddToCart,
    isCanAddToCard,
    stockAvailable,
    primitiveProduct,
    selectedPromotion,
    showBackInStockConfirmationModal,
    setShowBackInStockConfirmationModal,
    handleSubscribeToProductNotification,
  } = useProductDetailState();
  const showToast = useToast();
  const { onAddSingleItem } = useCartV2();

  const isValidPrice = useMemo(() => {
    if (!product) return false;
    if (price) return price?.finalPrice && price?.finalPrice >= 0;
  }, [product, price]);
  const showContact =
    !isValidPrice || isOutOfStock || !product?.status?.sellable;

  const trackingPayload = JSON.stringify({
    sku: product?.sellerSku,
    screenName: 'productDetail',
  });

  const onAddItemToCart = async (type: AddToCartType) => {
    if (requireLogin()) return;
    if (!isCanAddToCard) {
      showToast.info?.({
        title: '',
        message: t('Chỉ còn {{stockAvailable}} sản phẩm', {
          stockAvailable,
        }),
        type: 'error',
        icon: <CautionOutlined size={50} color="#E24C4B" />,
      });
      return;
    }
    if (!primitiveProduct) return;

    if (
      type === AddToCartType.ADDTOCART &&
      tryOpenComboPopupFromDetail({
        product,
        primitiveProduct,
        price,
        payloadAddToCart,
        selectedPromotion,
      })
    )
      return;

    // params send to Teko tracking in event addToCart
    const trackAddToCartParams: TrackAddToCartParams = {
      product: {
        sku: product?.sellerSku || '',
        name: product?.name || '',
        brand: product?.brand,
        categories: product?.categories,
        price: parseFloat(
          product?.price?.supplierRetailPrice?.toString() || '0',
        ),
        addToCartPrice: price?.finalPrice || 0,
        sellerSku: product?.sellerSku,
      },
      selectedPromotionId: selectedPromotion?.promotionId,
      type,
    };

    try {
      if (!payloadAddToCart?.sku) return;

      const res = await onAddSingleItem(
        {
          sku: payloadAddToCart?.sku,
          promotionId: selectedPromotion?.promotionId,
          quantity: payloadAddToCart?.quantity,
          isPrimary: payloadAddToCart?.isPrimary,
        },
        false,
        allowBuyNow && type === AddToCartType.BUYNOW
          ? IsolatedCartType.BUY_NOW
          : undefined,
      );
      if (res) {
        trackAddToCart({
          ...trackAddToCartParams,
          status: 'success',
          cartId: res.id,
        });
      } else {
        trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      }

      if (
        res &&
        (type === AddToCartType.BUYNOW || type === AddToCartType.INSTALLMENT)
      ) {
        if (type === AddToCartType.BUYNOW && !allowBuyNow) {
          router.push('/cart');
        } else {
          const key = getBuyNowKey(true);
          router.push(
            product?.isPreOrder
              ? `/checkout?bnk=${key}&isPreOrder=true`
              : `/checkout?bnk=${key}`,
          );
        }
      }
    } catch (e) {
      trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      logger.error(e);
    }
  };

  return (
    <>
      <div ref={actionButtonsRef}>
        {product?.isPreOrder ? (
          <StyledButton
            variant="primary"
            block
            disabled={!actionReady}
            onClick={() => onAddItemToCart(AddToCartType.BUYNOW)}
            borderRadius="m"
          >
            <SubTitle level={2} weight="medium">
              {t('Đặt trước')}
            </SubTitle>
          </StyledButton>
        ) : (
          <>
            {!showContact && (
              <Flex gap="s8">
                <AddToCartButton
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: 'addToCart',
                    target: 'productDetail',
                    regionName: 'descriptionDetail',
                    payload: trackingPayload,
                  })}
                >
                  <StyledButton
                    variant="secondary"
                    onClick={() => onAddItemToCart(AddToCartType.ADDTOCART)}
                    block
                    disabled={!actionReady}
                    borderRadius="m"
                  >
                    <SubTitle level={2} weight="medium">
                      {t('Thêm vào giỏ hàng')}
                    </SubTitle>
                  </StyledButton>
                </AddToCartButton>

                <BuyNowButton
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: 'buyNow',
                    regionName: 'descriptionDetail',
                    target: 'cart',
                    payload: trackingPayload,
                  })}
                >
                  <StyledButton
                    variant="primary"
                    block
                    disabled={!actionReady}
                    onClick={() => onAddItemToCart(AddToCartType.BUYNOW)}
                    borderRadius="m"
                  >
                    <SubTitle level={2} weight="medium">
                      {t('Mua ngay')}
                    </SubTitle>
                  </StyledButton>
                </BuyNowButton>
              </Flex>
            )}
            {showContact &&
              !!contactLink &&
              (!!epicOmni1551 && isLoggedIn ? (
                <Flex width="100%" gap="s12" align="center">
                  <StyledButton
                    borderRadius="m"
                    style={{ flex: 1 }}
                    variant="secondary"
                    onClick={(): void => {
                      window.open(contactLink);
                      return;
                    }}
                    {...getTrackingData({
                      isTrackContent: true,
                      regionName: 'descriptionDetail',
                      contentName: 'contactSale',
                      target: 'productDetail',
                      payload: trackingPayload,
                    })}
                  >
                    <SubTitle level={2} weight="medium">
                      {t('Liên hệ')}
                    </SubTitle>
                  </StyledButton>
                  <StyledButton
                    borderRadius="m"
                    style={{ flex: 1 }}
                    variant="primary"
                    onClick={handleSubscribeToProductNotification}
                    disabled={authLoading} // need to disable button until fetch user done. otherwise, handleSubscribeToProductNotification function will redirect to the login page.
                  >
                    <SubTitle level={2} weight="medium">
                      {t('Thông báo khi có hàng')}
                    </SubTitle>
                  </StyledButton>
                </Flex>
              ) : (
                <CustomLink href={contactLink} target="_blank">
                  <div
                    {...getTrackingData({
                      isTrackContent: true,
                      regionName: 'descriptionDetail',
                      contentName: 'contactSale',
                      target: 'productDetail',
                      payload: trackingPayload,
                    })}
                  >
                    <StyledButton variant="primary" block borderRadius="m">
                      <SubTitle level={2} weight="medium">
                        {t('Liên hệ')}
                      </SubTitle>
                    </StyledButton>
                  </div>
                </CustomLink>
              ))}
            {showContact &&
              !contactLink &&
              (!!epicOmni1551 && isLoggedIn ? (
                <Flex width="100%" gap="s12" align="center">
                  <StyledButton
                    style={{
                      flex: 1,
                      backgroundColor: '#0000000A',
                      border: '1px solid #00000026',
                    }}
                    variant="secondary"
                    disabled
                    borderRadius="m"
                  >
                    <SubTitle level={2} weight="medium" color="#00000040">
                      {t('Hết hàng')}
                    </SubTitle>
                  </StyledButton>
                  <StyledButton
                    style={{ flex: 1 }}
                    borderRadius="m"
                    variant="primary"
                    onClick={handleSubscribeToProductNotification}
                    disabled={authLoading} // need to disable button until fetch user done. otherwise, handleSubscribeToProductNotification function will redirect to the login page.
                  >
                    <SubTitle level={2} weight="medium">
                      {t('Thông báo khi có hàng')}
                    </SubTitle>
                  </StyledButton>
                </Flex>
              ) : (
                <StyledButton
                  disabled
                  block
                  style={{
                    backgroundColor: '#0000000A',
                    border: '1px solid #00000026',
                  }}
                  borderRadius="m"
                >
                  <SubTitle level={2} weight="medium" color="#00000040">
                    {t('Hết hàng')}
                  </SubTitle>
                </StyledButton>
              ))}
          </>
        )}
      </div>
      <BackInStockConfirmationModal
        showBackInStockConfirmationModal={showBackInStockConfirmationModal}
        setShowBackInStockConfirmationModal={
          setShowBackInStockConfirmationModal
        }
        productName={product?.name || ''}
        sku={product?.sku || ''}
      />
    </>
  );
};

const AddToCartButton = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
  border-radius: 8px;
`;

const BuyNowButton = styled.div`
  flex: 1;
`;

const StyledButton = styled(Button)(
  ({ height }: { height?: string }) => `
  height: ${height || '56px'};
`,
);
